import { ThemeEvents } from '@theme/events';

const TOAST_DURATION = 5000;
const EXIT_ANIMATION_MS = 280;
const ICON_ERROR_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="M10 1.667a8.333 8.333 0 1 0 0 16.666 8.333 8.333 0 0 0 0-16.666Zm0 4.166a.833.833 0 0 1 .833.834v3.333a.833.833 0 1 1-1.666 0V6.667A.833.833 0 0 1 10 5.833Zm0 8.334a1.042 1.042 0 1 1 0-2.084 1.042 1.042 0 0 1 0 2.084Z" fill="currentColor"/></svg>`;
const ICON_CLOSE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false"><path d="m3 3 6 6M9 3l-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;

class CartErrorToast extends HTMLElement {
  constructor() {
    super();
    this.toasts = new Map();
  }

  connectedCallback() {
    document.addEventListener(ThemeEvents.cartError, this.handleCartError);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartError, this.handleCartError);
  }

  handleCartError = (event) => {
    const detail = event?.detail?.data;
    if (!detail) return;

    const message = this.buildMessage(detail);
    if (!message) return;

    this.show(message);
  };

  buildMessage({ message, description, errors }) {
    const parts = [];
    if (description && typeof description === 'string' && description !== message) {
      parts.push(description);
    } else if (message) {
      parts.push(message);
    }

    if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
      for (const key of Object.keys(errors)) {
        const val = errors[key];
        if (Array.isArray(val)) {
          parts.push(val.join(' '));
        } else if (typeof val === 'string') {
          parts.push(val);
        }
      }
    }

    return parts.filter(Boolean).join(' ').trim();
  }

  show(message) {
    const existingKey = this.findToastByMessage(message);
    if (existingKey) {
      this.bump(existingKey);
      return;
    }

    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const toast = document.createElement('div');
    toast.className = 'cart-error-toast__item';
    toast.setAttribute('role', 'alert');
    toast.dataset.toastId = id;

    const icon = document.createElement('span');
    icon.className = 'cart-error-toast__icon';
    icon.innerHTML = ICON_ERROR_SVG;

    const text = document.createElement('p');
    text.className = 'cart-error-toast__text';
    text.textContent = message;

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'cart-error-toast__close';
    closeButton.setAttribute('aria-label', this.dataset.closeLabel || 'Close');
    closeButton.innerHTML = ICON_CLOSE_SVG;
    closeButton.addEventListener('click', () => this.dismiss(id));

    toast.appendChild(icon);
    toast.appendChild(text);
    toast.appendChild(closeButton);

    this.appendChild(toast);

    requestAnimationFrame(() => {
      toast.classList.add('cart-error-toast__item--visible');
    });

    const timeoutId = window.setTimeout(() => this.dismiss(id), TOAST_DURATION);
    this.toasts.set(id, { element: toast, timeoutId, message });
  }

  bump(id) {
    const entry = this.toasts.get(id);
    if (!entry) return;
    clearTimeout(entry.timeoutId);
    entry.element.classList.remove('cart-error-toast__item--pulse');
    void entry.element.offsetWidth;
    entry.element.classList.add('cart-error-toast__item--pulse');
    entry.timeoutId = window.setTimeout(() => this.dismiss(id), TOAST_DURATION);
  }

  dismiss(id) {
    const entry = this.toasts.get(id);
    if (!entry) return;
    clearTimeout(entry.timeoutId);
    entry.element.classList.remove('cart-error-toast__item--visible');
    entry.element.classList.add('cart-error-toast__item--leaving');
    window.setTimeout(() => {
      entry.element.remove();
      this.toasts.delete(id);
    }, EXIT_ANIMATION_MS);
  }

  findToastByMessage(message) {
    for (const [id, entry] of this.toasts) {
      if (entry.message === message) return id;
    }
    return null;
  }
}

if (!customElements.get('cart-error-toast')) {
  customElements.define('cart-error-toast', CartErrorToast);
}
